import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface DossierSelectionContextValue {
  selectedIds: Set<string>;
  toggleSelection: (id: string) => void;
  toggleSelectAll: () => void;
  deselectAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  isSelected: (id: string) => boolean;
}

const DossierSelectionContext = createContext<DossierSelectionContextValue | null>(null);

export const useDossierSelection = (): DossierSelectionContextValue => {
  const ctx = useContext(DossierSelectionContext);
  if (!ctx) {
    throw new Error('useDossierSelection must be used within DossierSelectionProvider');
  }
  return ctx;
};

interface DossierSelectionProviderProps {
  allDossierIds: string[];
  children: React.ReactNode;
}

export const DossierSelectionProvider = ({ allDossierIds, children }: DossierSelectionProviderProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSel = allDossierIds.length > 0 && allDossierIds.every((id) => prev.has(id));
      if (allSel) {
        return new Set<string>();
      }
      return new Set(allDossierIds);
    });
  }, [allDossierIds]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const allSelected =
    allDossierIds.length > 0 && allDossierIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const value = useMemo<DossierSelectionContextValue>(
    () => ({
      selectedIds,
      toggleSelection,
      toggleSelectAll,
      deselectAll,
      allSelected,
      someSelected,
      isSelected,
    }),
    [
      selectedIds,
      toggleSelection,
      toggleSelectAll,
      deselectAll,
      allSelected,
      someSelected,
      isSelected,
    ],
  );

  return (
    <DossierSelectionContext.Provider value={value}>
      {children}
    </DossierSelectionContext.Provider>
  );
};